import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Text,
    Heading,
    Spinner,
    SimpleGrid,
    Link,
    Badge,
    VStack,
    HStack,
    Flex,
    Divider,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faCheckCircle,
    faUser,
    faEnvelope,
    faPhone,
    faPassport,
    faEuroSign,
    faExternalLinkAlt,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { getAllForeignPartnersBookings } from '../../services/bookings';
import Pagination from '../../components/pagination/Pagination';
import { useTranslation } from 'react-i18next';

const DetailRow = ({ icon, label, value }) => (
    <HStack spacing={3} align="flex-start">
        <Flex
            w="28px"
            h="28px"
            align="center"
            justify="center"
            borderRadius="md"
            bg="gray.50"
            color="teal.600"
            flexShrink={0}
        >
            <FontAwesomeIcon icon={icon} size="sm" />
        </Flex>
        <Box flex={1} minW={0}>
            <Text fontSize="xs" color="gray.500" lineHeight="short">{label}</Text>
            <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={2}>
                {value}
            </Text>
        </Box>
    </HStack>
);

const ForeignPartnerBookings = () => {
    const { t } = useTranslation('common');
    const [currentPage, setCurrentPage] = useState(1);
    const [allBookings, setAllBookings] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);

    const { data, isLoading, error, isFetching } = useQuery({
        queryKey: ['get-all-foreign-partners-bookings', currentPage],
        queryFn: () => getAllForeignPartnersBookings({ page: currentPage }),
        select: (data) => data.data,
        refetchOnWindowFocus: false,
        enabled: true,
    });

    useEffect(() => {
        if (!data?.foreignPartnerBookings) return;

        setAllBookings((prev) => {
            if (currentPage === 1) {
                return data.foreignPartnerBookings;
            }

            const merged = [...prev, ...data.foreignPartnerBookings];
            const dedupedById = [];
            const seen = new Set();

            for (const booking of merged) {
                if (seen.has(booking.id)) continue;
                seen.add(booking.id);
                dedupedById.push(booking);
            }

            return dedupedById;
        });

        setHasNextPage(data?.pagination?.nextPage !== null);
    }, [data, currentPage]);

    const translateStatus = (status) => {
        const key = status?.toLowerCase();
        if (key === 'pending') return t('affiliatePending');
        if (key === 'approved') return t('affiliateApproved');
        return status;
    };

    const formatReservationCount = (count) =>
        count === 1
            ? t('affiliateReservationCountOne', { count })
            : t('affiliateReservationCountMany', { count });

    if (isLoading && currentPage === 1) {
        return (
            <Flex justify="center" align="center" minH="50vh">
                <Spinner size="xl" color="teal.500" thickness="3px" />
            </Flex>
        );
    }

    if (error) {
        return (
            <Box bg="gray.50" minH="calc(100vh - 80px)" py={10}>
                <Box maxW="900px" mx="auto" px={4} textAlign="center">
                    <Text color="gray.600">{t('affiliateNoBookingsYet')}</Text>
                </Box>
            </Box>
        );
    }

    if (!allBookings.length) {
        return (
            <Box bg="gray.50" minH="calc(100vh - 80px)" py={{ base: 6, md: 10 }}>
                <Box maxW="900px" mx="auto" px={{ base: 4, md: 6 }}>
                    <Heading size="lg" color="teal.600" mb={2}>
                        {t('affiliateBookings')}
                    </Heading>
                    <Box
                        mt={8}
                        textAlign="center"
                        py={12}
                        bg="white"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="gray.200"
                    >
                        <Text color="gray.500">{t('affiliateNoReservationsYet')}</Text>
                    </Box>
                </Box>
            </Box>
        );
    }

    const awaitingPriceBookings = allBookings.filter(
        (b) => b.booking?.currentStatus?.toLowerCase() === 'draft'
    );
    const pendingBookings = allBookings.filter(
        (b) => b.booking?.currentStatus?.toLowerCase() === 'pending'
    );
    const approvedBookings = allBookings.filter(
        (b) => b.booking?.currentStatus?.toLowerCase() === 'approved'
    );

    const bookingUrl = (uuid, scrollToPrice = false) =>
        `${process.env.REACT_APP_BASE_URL}/book/${uuid}${
            scrollToPrice ? '#set-affiliate-price' : ''
        }`;

    const renderAwaitingPriceCard = (booking) => (
        <Box
            key={booking.id}
            bg="white"
            borderWidth="2px"
            borderColor="orange.300"
            borderRadius="xl"
            p={5}
            boxShadow="md"
        >
            <HStack justify="space-between" align="flex-start" mb={3}>
                <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                        {t('affiliateBookingLabel')}
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="teal.600">
                        {booking.referenceNumber}
                    </Text>
                </Box>
                <Badge colorScheme="orange" variant="solid" borderRadius="full" px={3} py={1} fontSize="xs">
                    {t('affiliateAwaitingPrice')}
                </Badge>
            </HStack>

            <Text fontSize="sm" color="gray.600" mb={4}>
                {t('affiliateAwaitingPriceHint')}
            </Text>

            <VStack spacing={2} align="stretch" mb={4}>
                <DetailRow icon={faUser} label={t('affiliateClient')} value={booking.clientName} />
                <DetailRow
                    icon={faEuroSign}
                    label={t('affiliateBasePrice')}
                    value={`EUR ${booking.booking?.fullPrice}`}
                />
            </VStack>

            <Link
                isExternal
                href={bookingUrl(booking.booking.uuid, true)}
                display="block"
                w="full"
                py={3}
                px={4}
                bg="teal.500"
                color="white"
                fontWeight="semibold"
                fontSize="sm"
                textAlign="center"
                borderRadius="lg"
                _hover={{ bg: 'teal.600', textDecoration: 'none', color: 'white' }}
            >
                <HStack justify="center" spacing={2}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                    <Text>{t('affiliateSetPriceNow', { id: booking.referenceNumber })}</Text>
                </HStack>
            </Link>
        </Box>
    );

    const renderBookingCard = (booking) => (
        <Box
            key={booking.id}
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            p={5}
            boxShadow="sm"
            transition="all 0.2s"
            _hover={{
                borderColor: 'teal.200',
                boxShadow: 'md',
                transform: 'translateY(-2px)',
            }}
        >
            <HStack justify="space-between" align="flex-start" mb={4}>
                <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                        {t('affiliateBookingLabel')}
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="teal.600">
                        {booking.referenceNumber}
                    </Text>
                </Box>
                <Badge
                    colorScheme={
                        booking.booking?.currentStatus?.toLowerCase() === 'approved'
                            ? 'green'
                            : 'orange'
                    }
                    variant="subtle"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="xs"
                >
                    {translateStatus(booking.booking?.currentStatus)}
                </Badge>
            </HStack>

            <Divider mb={4} />

            <VStack spacing={3} align="stretch" mb={4}>
                <DetailRow
                    icon={faUser}
                    label={t('affiliateClient')}
                    value={booking.clientName}
                />
                <DetailRow
                    icon={faEnvelope}
                    label={t('affiliateEmail')}
                    value={booking.email}
                />
                <DetailRow
                    icon={faPhone}
                    label={t('affiliatePhone')}
                    value={booking.telephoneNumber}
                />
                <DetailRow
                    icon={faPassport}
                    label={t('affiliateNameOnPassport')}
                    value={booking.nameOnPassport}
                />
                <DetailRow
                    icon={faPassport}
                    label={t('affiliatePassportExpiration')}
                    value={booking.passportExpirationDate || t('affiliateNotAvailable')}
                />
            </VStack>

            <Box bg="teal.50" borderRadius="lg" p={3} mb={4}>
                <HStack justify="space-between" mb={2}>
                    <HStack spacing={2} color="gray.600" fontSize="sm">
                        <FontAwesomeIcon icon={faEuroSign} />
                        <Text>{t('affiliateBasePrice')}</Text>
                    </HStack>
                    <Text fontWeight="semibold" color="gray.800">
                        EUR {booking.booking?.fullPrice}
                    </Text>
                </HStack>
                <HStack justify="space-between">
                    <HStack spacing={2} color="teal.700" fontSize="sm">
                        <FontAwesomeIcon icon={faEuroSign} />
                        <Text fontWeight="medium">{t('affiliatePrice')}</Text>
                    </HStack>
                    <Text fontWeight="bold" color="teal.600">
                        EUR {booking.affiliatePrice}
                    </Text>
                </HStack>
                <HStack justify="space-between" pt={2} borderTopWidth="1px" borderColor="teal.100">
                    <HStack spacing={2} color="teal.800" fontSize="sm">
                        <FontAwesomeIcon icon={faEuroSign} />
                        <Text fontWeight="medium">{t('bookingCommission')}</Text>
                    </HStack>
                    <Text fontWeight="bold" color="teal.700">
                        {booking.commission != null
                            ? `EUR ${booking.commission}`
                            : t('affiliateNotAvailable')}
                    </Text>
                </HStack>
            </Box>

            <Link
                isExternal
                href={bookingUrl(booking.booking.uuid)}
                color="teal.600"
                fontSize="sm"
                fontWeight="medium"
                display="flex"
                alignItems="center"
                gap={2}
                _hover={{ color: 'teal.700', textDecoration: 'none' }}
            >
                <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                {t('affiliateViewBookingDetails', { id: booking.referenceNumber })}
            </Link>
        </Box>
    );

    const SectionHeader = ({ icon, title, count, colorScheme }) => (
        <HStack spacing={3} mb={5}>
            <Flex
                align="center"
                justify="center"
                w="36px"
                h="36px"
                borderRadius="full"
                bg={`${colorScheme}.50`}
                color={`${colorScheme}.600`}
            >
                <FontAwesomeIcon icon={icon} />
            </Flex>
            <Box>
                <Heading size="md" color="gray.800">{title}</Heading>
                <Text fontSize="sm" color="gray.500">
                    {formatReservationCount(count)}
                </Text>
            </Box>
        </HStack>
    );

    return (
        <Pagination
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            onShowMore={() => setCurrentPage((prev) => prev + 1)}
        >
            <Box bg="gray.50" minH="calc(100vh - 80px)" py={{ base: 6, md: 10 }}>
                <Box maxW="1200px" mx="auto" px={{ base: 4, md: 6 }}>
                    <Box mb={8}>
                        <Heading size="lg" color="teal.600" mb={2}>
                            {t('affiliateBookings')}
                        </Heading>
                        <Text color="gray.600" fontSize="sm">
                            {t('affiliateBookingsSubtitle')}
                        </Text>
                    </Box>

                    {awaitingPriceBookings.length > 0 && (
                        <Box mb={10}>
                            <SectionHeader
                                icon={faExclamationTriangle}
                                title={t('affiliateAwaitingPrice')}
                                count={awaitingPriceBookings.length}
                                colorScheme="orange"
                            />
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                                {awaitingPriceBookings.map(renderAwaitingPriceCard)}
                            </SimpleGrid>
                        </Box>
                    )}

                    {awaitingPriceBookings.length > 0 && <Divider mb={10} />}

                    <Box mb={10}>
                        <SectionHeader
                            icon={faClock}
                            title={t('affiliatePending')}
                            count={pendingBookings.length}
                            colorScheme="orange"
                        />
                        {pendingBookings.length ? (
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                                {pendingBookings.map(renderBookingCard)}
                            </SimpleGrid>
                        ) : (
                            <Box
                                py={8}
                                textAlign="center"
                                bg="white"
                                borderRadius="xl"
                                borderWidth="1px"
                                borderColor="gray.200"
                            >
                                <Text color="gray.500" fontSize="sm">
                                    {t('affiliateNoPendingBookings')}
                                </Text>
                            </Box>
                        )}
                    </Box>

                    <Divider mb={10} />

                    <Box>
                        <SectionHeader
                            icon={faCheckCircle}
                            title={t('affiliateApproved')}
                            count={approvedBookings.length}
                            colorScheme="teal"
                        />
                        {approvedBookings.length ? (
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                                {approvedBookings.map(renderBookingCard)}
                            </SimpleGrid>
                        ) : (
                            <Box
                                py={8}
                                textAlign="center"
                                bg="white"
                                borderRadius="xl"
                                borderWidth="1px"
                                borderColor="gray.200"
                            >
                                <Text color="gray.500" fontSize="sm">
                                    {t('affiliateNoApprovedBookings')}
                                </Text>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Pagination>
    );
};

export default ForeignPartnerBookings;
